export const normalizeJournalNameKey = (value) => (
  typeof value === 'string' ? value.trim().toLowerCase() : ''
);

const entryTimestamp = (entry) => {
  if (typeof entry?.updatedAt === 'number') return entry.updatedAt;
  if (typeof entry?.createdAt === 'number') return entry.createdAt;
  const parsed = Date.parse(entry?.updatedAt || entry?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

export const mergeJournalEntrySources = (...sources) => {
  const byId = new Map();
  for (const source of sources) {
    const entries = Array.isArray(source?.page) ? source.page : source;
    for (const entry of Array.isArray(entries) ? entries : []) {
      if (!entry || entry.deleted || entry.isDeleted) continue;
      const id = typeof entry.id === 'string' && entry.id.trim() ? entry.id.trim() : '';
      if (!id || byId.has(id)) continue;
      byId.set(id, entry);
    }
  }
  return [...byId.values()].sort((a, b) => entryTimestamp(b) - entryTimestamp(a));
};

export const findJournalEntryByCategoryName = (entries, { category, name, nameKey }) => {
  const categoryKey = typeof category === 'string' ? category.trim() : '';
  const targetNameKey = normalizeJournalNameKey(nameKey || name || '');
  if (!categoryKey || !targetNameKey) return null;
  return mergeJournalEntrySources(entries).find(entry => (
    entry.category === categoryKey
    && normalizeJournalNameKey(entry.name || '') === targetNameKey
  )) || null;
};

export const listJournalSessions = (entries) => (
  mergeJournalEntrySources(entries)
    .filter(entry => entry.category === 'session')
    .sort((a, b) => {
      const bNumber = Number.isFinite(b.sessionNumber) ? b.sessionNumber : null;
      const aNumber = Number.isFinite(a.sessionNumber) ? a.sessionNumber : null;
      if (bNumber !== null && aNumber !== null && bNumber !== aNumber) return bNumber - aNumber;
      return entryTimestamp(b) - entryTimestamp(a);
    })
);

export const getActiveJournalSession = (entries, {
  manualSessionId = null,
  now = Date.now(),
  activeWindowMs = 12 * 60 * 60 * 1000,
} = {}) => {
  const sessions = listJournalSessions(entries);
  if (manualSessionId) {
    const manual = sessions.find(entry => entry.id === manualSessionId);
    if (manual) return manual;
  }
  const latest = sessions.slice().sort((a, b) => entryTimestamp(b) - entryTimestamp(a))[0] || null;
  if (!latest) return null;
  return now - entryTimestamp(latest) < activeWindowMs ? latest : null;
};

const stripPrivateBlocks = (value) => (
  typeof value === 'string'
    ? value.replace(/\{\{PRIVATE:[^}]*\}\}[\s\S]*?\{\{\/PRIVATE\}\}/g, '')
    : ''
);

const collectEntrySearchText = (entry, { includePrivate = false } = {}) => {
  const raw = [
    entry?.name,
    entry?.description,
    entry?.summary,
    entry?.personality,
    entry?.appearance,
    entry?.habitat,
    ...(Array.isArray(entry?.tags) ? entry.tags : []),
    ...(Array.isArray(entry?.locationTags) ? entry.locationTags : []),
    ...(Array.isArray(entry?.objectives) ? entry.objectives.map(o => o?.text) : []),
    ...(Array.isArray(entry?.npcsEncountered) ? entry.npcsEncountered : []),
    ...(Array.isArray(entry?.creaturesEncountered) ? entry.creaturesEncountered : []),
    ...(Array.isArray(entry?.itemsFound) ? entry.itemsFound : []),
  ].filter(Boolean).join(' ');
  return includePrivate ? raw : stripPrivateBlocks(raw);
};

export const searchJournalEntries = (entries, query, {
  mode = 'title',
  includePrivate = false,
  excludeEntryId = null,
  limit = 10,
} = {}) => {
  const q = normalizeJournalNameKey(query);
  if (!q) return [];
  const normalizedLimit = Math.max(1, Math.min(50, Number.isFinite(limit) ? Math.trunc(limit) : 10));
  const candidates = mergeJournalEntrySources(entries).filter(entry => entry.id !== excludeEntryId);
  const scored = [];
  for (const entry of candidates) {
    const name = normalizeJournalNameKey(entry.name || '');
    if (mode === 'title') {
      if (!name.includes(q)) continue;
      scored.push({
        entry,
        score: name === q ? 300 : (name.startsWith(q) ? 200 : 100),
        snippet: entry.name || '',
      });
      continue;
    }
    const text = collectEntrySearchText(entry, { includePrivate });
    const lower = normalizeJournalNameKey(text);
    if (!lower.includes(q)) continue;
    const index = lower.indexOf(q);
    scored.push({
      entry,
      score: index === 0 ? 120 : 80,
      snippet: text.slice(Math.max(0, index - 30), index + q.length + 50),
    });
  }
  return scored
    .sort((a, b) => b.score - a.score || normalizeJournalNameKey(a.entry.name).localeCompare(normalizeJournalNameKey(b.entry.name)))
    .slice(0, normalizedLimit);
};
